'use client'

import { createContext, useContext, useMemo, useState } from 'react'

import type { ReactNode } from 'react'

import {
  rankedNavItems,
  MOBILE_VISIBLE_COUNT,
  type NavItem
} from '@/features/navigation'

interface NavOverflowContextValue {
  overflowItems: NavItem[]
  setOverflowItems: (items: NavItem[]) => void
}

// Matches the mobile bottom nav's fixed slice — sensible default on mobile
// (which never measures) and before the desktop rail's first measurement.
const DEFAULT_OVERFLOW_ITEMS = rankedNavItems.slice(MOBILE_VISIBLE_COUNT)

const NavOverflowContext = createContext<NavOverflowContextValue | null>(null)

export function useNavOverflow(): NavOverflowContextValue {
  const ctx = useContext(NavOverflowContext)
  if (!ctx) {
    throw new Error('useNavOverflow must be used within a NavOverflowProvider')
  }
  return ctx
}

interface NavOverflowProviderProps {
  children: ReactNode
}

export function NavOverflowProvider({ children }: NavOverflowProviderProps) {
  const [overflowItems, setOverflowItems] = useState<NavItem[]>(
    DEFAULT_OVERFLOW_ITEMS
  )

  const contextValue = useMemo<NavOverflowContextValue>(
    () => ({ overflowItems, setOverflowItems }),
    [overflowItems]
  )

  return (
    <NavOverflowContext.Provider value={contextValue}>
      {children}
    </NavOverflowContext.Provider>
  )
}
